import { useState, useEffect } from 'react';

import { Button, Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import RToggleButtons from '@/components/RToggleButtons';
import Tooltip from '@/components/RTooltip';
import { SimulationResult } from '@/types/simulations';

import AncillaryTable from './AncillaryTable';
import ExcessTable from './ExcessTable';
import StorageTable from './StorageTable';

interface AnalysisTableProps {
  activeTab?: number;
  simulationId?: number;
  showNumbers?: boolean;
}

const AnalysisTable = ({
  activeTab,
  simulationId,
  showNumbers = false,
}: AnalysisTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const {
    data: simulationData,
    isLoading,
    error,
  } = useSWR<SimulationResult>(`/simulation/${resultId}/`, {
    revalidateOnFocus: false,
  });
  const buttons = [
    t('result.analysis.tabs.excessEnergy'),
    t('result.analysis.tabs.storagePremium'),
    t('result.analysis.tabs.ancillaryServicesAnalysis'),
  ];
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    if (typeof activeTab === 'number') {
      setActiveIndex(activeTab);
    }
  }, [activeTab]);

  const handleButtonClick = (index: number) => {
    setActiveIndex(index);
  };
  const getTitle = () => {
    if (activeIndex === 0) return t('result.analysis.excessEnergy.title');
    if (activeIndex === 1) return t('result.analysis.storagePremium.title');
    if (activeIndex === 2) return t('result.analysis.ancillaryServices.title');
    return '';
  };
  const getDescription = () => {
    if (activeIndex === 0) return t('result.analysis.excessEnergy.description');
    if (activeIndex === 1)
      return t('result.analysis.storagePremium.description');
    if (activeIndex === 2)
      return t('result.analysis.ancillaryServices.description');
    return '';
  };
  return (
    <Card
      title={
        <Flex gap={8} align="center">
          <Typography.Title level={4} className="fs-17-bold">
            {activeIndex === 0 && t('result.analysis.excessEnergy.title')}
            {activeIndex === 1 && t('result.analysis.storagePremium.title')}
            {activeIndex === 2 && t('result.analysis.ancillaryServices.title')}
          </Typography.Title>
          <Tooltip title={getTitle()} description={getDescription()}>
            <IconInfo />
          </Tooltip>
        </Flex>
      }
      extra={
        <Flex gap={16} align="center">
          <RToggleButtons
            labels={buttons}
            initialActiveIndex={activeTab}
            onButtonClick={handleButtonClick}
          />
          <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
        </Flex>
      }
      styles={{ header: { padding: '14px 24px' } }}
      $paddingBody="12px 0"
      style={{ width: '100%', marginTop: '24px', minWidth: '100%' }}
    >
      {isLoading ? (
        <Flex justify="center" align="center" style={{ height: 220 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      ) : error ? (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 220 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('common.errorFetchingData')}
          </Typography.Text>
        </Flex>
      ) : (
        <>
          {activeIndex === 0 && simulationData && (
            <ExcessTable simulationData={simulationData} />
          )}
          {activeIndex === 1 && simulationData && (
            <StorageTable
              simulationData={simulationData}
              showNumbers={showNumbers}
            />
          )}
          {activeIndex === 2 && simulationData && (
            <AncillaryTable
              simulationData={simulationData}
              simulationId={simulationId}
            />
          )}
        </>
      )}
    </Card>
  );
};
export default AnalysisTable;
